import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAppMode, setAppModeRemote } from '../../../services/adminApi';
import '../catalog/catalog.css';
import './ops.css';

// Mode du site : un seul état test/production pour Stripe (vrais paiements), les imprimeurs (vraies commandes facturées et fabriquées),
// les factures et le site lui-même. Le tableau dit ce que fait chaque mode, service par service ; la bascule se confirme DANS la page,
// et dit ce qui change. Passer en production exige en plus de taper une phrase : un clic de trop ne suffit pas.
// Le bouton reste cliquable quand des clés « live » manquent, pour que la liste de ce qui manque soit visible : c'est la confirmation
// qui refuse d'avancer, jamais une bascule « à moitié prête » (le serveur refuse aussi).
const PHRASE = 'ACTIVER LA PRODUCTION';

export default function ModeSection() {
  const { t } = useTranslation('admin');
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState('');
  const [cible, setCible] = useState(null); // 'production' | 'test' | null
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState('');

  const charger = useCallback(async () => {
    try { setEtat(await fetchAppMode()); setErreur(''); } catch (err) { setErreur(err?.message || t('mode.loadFailed')); }
  }, [t]);
  useEffect(() => { charger(); }, [charger]);

  const ouvrir = (mode) => { setCible(mode); setSaisie(''); setErreur(''); setMessage(''); };
  const fermer = () => { setCible(null); setSaisie(''); };

  const confirmer = async () => {
    setEnCours(true);
    setErreur('');
    try {
      const r = await setAppModeRemote(cible);
      setMessage(r.mode === 'production' ? t('mode.nowProduction') : t('mode.nowTest'));
      fermer();
      await charger();
    } catch (err) {
      setErreur(err?.message || t('mode.switchFailed'));
      if (Array.isArray(err?.missing)) setEtat((p) => ({ ...p, missingForProduction: err.missing }));
    } finally {
      setEnCours(false);
    }
  };

  if (erreur && !etat) return <p className="adm-error" role="alert">{erreur}</p>;
  if (!etat) return <p className="adm-state" role="status">{t('catalog.common.loading')}</p>;

  const production = etat.mode === 'production';
  const manquants = etat.missingForProduction || [];
  const services = Array.isArray(etat.services) ? etat.services : [];

  return (
    <div className="adm-page-gap">
      <section className="adm-card">
        <div className="adm-modecard">
          <span className={`adm-modepill ${production ? 'is-prod' : 'is-test'}`}><i aria-hidden="true" />{production ? t('mode.productionReal') : t('mode.testMode')}</span>
          <p className="adm-note">{production ? t('mode.descProduction') : t('mode.descTest')}</p>
          <button type="button" className={`btn btn-sm ${production ? 'btn-outline' : 'btn-primary'}`} onClick={() => ouvrir(production ? 'test' : 'production')} disabled={enCours}>
            {production ? t('mode.toTest') : t('mode.toProduction')}
          </button>
        </div>

        {!production && manquants.length > 0 && <p className="adm-error" role="status">{t('mode.missing', { list: manquants.join(' · ') })}</p>}
        {message && <p className="adm-note adm-flash" role="status">{message}</p>}
        {erreur && !cible && <p className="adm-error" role="alert">{erreur}</p>}

        {cible && (
          <div className={`adm-switchpanel${cible === 'production' ? ' is-danger' : ''}`} role="alertdialog" aria-labelledby="adm-switch-title">
            <h3 id="adm-switch-title">{cible === 'production' ? t('mode.confirmProductionTitle') : t('mode.confirmTestTitle')}</h3>
            <p>{t('mode.whatChanges')}</p>
            <ul>
              {services.map((s) => <li key={s.id}><b>{s.label}</b> : {cible === 'production' ? s.production : s.test}{cible === 'production' && s.note ? ` ${s.note}` : ''}</li>)}
            </ul>
            {cible === 'production' && manquants.length > 0 && <p className="adm-error">{t('mode.missing', { list: manquants.join(' · ') })}</p>}
            {cible === 'production' && (
              <>
                <p>{t('mode.typePhrase')} <span className="adm-phrase">{PHRASE}</span></p>
                <label className="adm-sr" htmlFor="adm-mode-phrase">{t('mode.phraseLabel')}</label>
                <input id="adm-mode-phrase" className="adm-input" value={saisie} onChange={(e) => setSaisie(e.target.value)} placeholder={PHRASE} autoComplete="off" autoFocus />
              </>
            )}
            {cible === 'test' && <p className="adm-note">{t('mode.closesSite')}</p>}
            {erreur && <p className="adm-error" role="alert">{erreur}</p>}
            <div className="adm-actions">
              <button type="button" className={`btn btn-sm ${cible === 'production' ? 'adm-btn-danger' : 'btn-primary'}`} onClick={confirmer} disabled={enCours || (cible === 'production' && (saisie.trim() !== PHRASE || manquants.length > 0))}>
                {enCours ? t('mode.working') : t('mode.confirm')}
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={fermer} disabled={enCours}>{t('confirm.no')}</button>
            </div>
            <p className="adm-note">{t('mode.journaled')}</p>
          </div>
        )}
      </section>

      {services.length > 0 && (
        <div className="adm-tbl">
          <table>
            <thead>
              <tr>
                <th>{t('mode.table.what')}</th>
                <th className={production ? '' : 'is-current'}>{t('mode.table.test')}{production ? '' : ` ${t('mode.table.current')}`}</th>
                <th className={production ? 'is-current' : ''}>{t('mode.table.production')}{production ? ` ${t('mode.table.current')}` : ''}</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id}>
                  <td><b>{s.label}</b></td>
                  <td className={production ? '' : 'is-current'}>{s.test}</td>
                  <td className={production ? 'is-current' : ''}>{s.production}{s.note ? <small>{s.note}</small> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
